import type { CrudAdapter } from '@botto/core';
import { useCrudDataSource } from '@botto/core';
import { smsClient } from '../client';
import type { Partner, PartnerCreate, PartnerUpdate } from '../types';

function toDateString(value: string | Date | null | undefined): string {
  if (value == null || value === '') return '';
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }
  return String(value).slice(0, 10);
}

export const partnersAdapter: CrudAdapter<Partner, PartnerCreate, PartnerUpdate> = {
  async list({ offset, limit, sortName, sortDirection, filter }) {
    const { data, error } = await smsClient.GET('/partners/', {
      params: {
        query: {
          offset,
          limit,
          'sort.name': sortName ?? 'id',
          'sort.direction': (sortDirection ?? 'asc') as 'asc' | 'desc',
          archived: typeof filter?.archived === 'boolean' ? filter.archived : undefined,
        },
      },
    });
    if (error) throw error;
    return { items: data?.items ?? [], total: data?.total ?? 0 };
  },
  async create(payload) {
    const { data, error } = await smsClient.POST('/partners/', {
      body: {
        currency: payload.currency,
        legal_entity: payload.legal_entity,
        domain: payload.domain,
        contract_number: payload.contract_number,
        contract_date: toDateString(payload.contract_date as string | Date),
        archived: payload.archived ?? false,
        email: payload.email ?? null,
        password: payload.password ?? null,
        user_id: payload.user_id ?? null,
      },
    });
    if (error) throw error;
    return { id: data?.id ?? 0 };
  },
  async update(payload) {
    const { data, error } = await smsClient.PUT('/partners/', {
      body: {
        id: payload.id,
        currency: payload.currency,
        legal_entity: payload.legal_entity,
        domain: payload.domain,
        contract_number: payload.contract_number,
        contract_date: payload.contract_date
          ? toDateString(payload.contract_date as string | Date)
          : payload.contract_date,
        archived: payload.archived,
        user_id: payload.user_id,
      },
    });
    if (error) throw error;
    return (data ?? payload) as Partner;
  },
  async byKey(id) {
    const { data, error } = await smsClient.GET('/partners/{item_id}', {
      params: { path: { item_id: Number(id) } },
    });
    if (error) throw error;
    return data as Partner;
  },
  async remove(id) {
    const { error } = await smsClient.DELETE('/partners/{item_id}', {
      params: { path: { item_id: Number(id) } },
    });
    if (error) throw error;
  },
};

export const partnersStore = useCrudDataSource(partnersAdapter);
